import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/AdminLayout";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound, UserX, UserCheck, Trash2, Edit, Copy } from "lucide-react";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin: "Admin",
  team_manager: "Team Manager",
  content_writer: "Content Writer",
  sales: "Sales",
  customer: "Customer",
};

const UserManagement = () => {
  const { user, authLoading } = useApp();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [devLink, setDevLink] = useState(null);

  const canManage = user && (user.role === "super_admin" || user.role === "admin");

  const load = async () => {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/admin/users"), api.get("/admin/roles")]);
      setUsers(u.data);
      setRoles(r.data);
    } catch (e) {
      toast.error("Failed to load", { description: e.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "User Management — Dream Home Plans";
    if (canManage) load();
  }, [canManage]);

  if (authLoading) return <Layout><div className="p-24 text-center">Loading…</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canManage) return <Layout><div className="p-24 text-center text-muted-foreground">You don't have permission to manage users.</div></Layout>;

  const resetPassword = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/reset-password`);
      toast.success("Reset link sent", { description: `A password reset email has been sent to ${u.email}.` });
      if (data.dev_reset_url) setDevLink({ user: u, url: data.dev_reset_url });
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.user_id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? "User deactivated" : "User activated");
      load();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    }
  };

  return (
    <Layout title="User Management">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="overline text-accent">Administration</div>
            <p className="mt-2 text-muted-foreground text-sm">Create, edit, activate/deactivate, delete users, reset passwords, and assign roles.</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-none bg-accent hover:bg-accent/90 h-12 px-6" data-testid="create-user-btn">
            <Plus size={16} className="mr-2" /> Create user
          </Button>
        </div>

        {devLink && (
          <div className="mb-8 border border-accent bg-accent/10 p-4 flex items-start gap-3" data-testid="dev-link-banner">
            <div className="flex-1 text-xs">
              <div className="overline text-accent mb-1">Dev mode reset link for {devLink.user.email}</div>
              <div className="break-all">{devLink.url}</div>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(devLink.url); toast.success("Copied"); }} className="p-1 hover:text-accent">
              <Copy size={14} />
            </button>
            <button onClick={() => setDevLink(null)} className="text-xs">✕</button>
          </div>
        )}

        <div className="border border-border" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-left">
                <th className="px-4 py-3 overline">Name</th>
                <th className="px-4 py-3 overline">Email</th>
                <th className="px-4 py-3 overline">Role</th>
                <th className="px-4 py-3 overline">Status</th>
                <th className="px-4 py-3 overline text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && users.map((u) => (
                <tr key={u.user_id} className="border-t border-border" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div>{u.name}</div>
                    {u.title && <div className="text-xs text-muted-foreground">{u.title}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-1 text-xs bg-muted font-medium">{ROLE_LABELS[u.role] || u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${u.is_active ? "text-primary" : "text-muted-foreground"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? "bg-primary" : "bg-muted-foreground"}`} />
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button title="Edit" onClick={() => setEditUser(u)} className="p-2 hover:text-accent" data-testid={`edit-${u.user_id}`}><Edit size={14} /></button>
                      <button title="Send reset" onClick={() => resetPassword(u)} className="p-2 hover:text-accent" data-testid={`reset-${u.user_id}`}><KeyRound size={14} /></button>
                      <button title={u.is_active ? "Deactivate" : "Activate"} onClick={() => toggleActive(u)} className="p-2 hover:text-accent" data-testid={`toggle-${u.user_id}`}>
                        {u.is_active ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                      {u.user_id !== user.user_id && (
                        <button title="Delete" onClick={() => removeUser(u)} className="p-2 hover:text-destructive" data-testid={`delete-${u.user_id}`}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Role summary */}
        <div className="mt-14">
          <h2 className="font-display text-3xl mb-6">Roles &amp; permissions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {roles.map((r) => (
              <div key={r.role} className="border border-border p-5" data-testid={`role-card-${r.role}`}>
                <div className="font-display text-xl">{ROLE_LABELS[r.role] || r.role}</div>
                <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                <div className="mt-3 flex flex-wrap gap-1">
                  {r.permissions.slice(0, 6).map((p) => (
                    <span key={p} className="text-[10px] bg-muted px-2 py-0.5 font-mono">{p}</span>
                  ))}
                  {r.permissions.length > 6 && (
                    <span className="text-[10px] text-muted-foreground">+{r.permissions.length - 6} more</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={(devUrl, u) => { load(); if (devUrl) setDevLink({ user: u, url: devUrl }); }} roles={roles} isSuperAdmin={user.role === "super_admin"} />
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={() => { load(); setEditUser(null); }} roles={roles} currentUser={user} />
    </Layout>
  );
};

const CreateUserDialog = ({ open, onClose, onSaved, roles, isSuperAdmin }) => {
  const [form, setForm] = useState({ name: "", email: "", role: "team_manager", title: "", is_active: true });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const availableRoles = roles.filter((r) => isSuperAdmin || !["super_admin", "admin"].includes(r.role));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success("User created", { description: `${form.name} added as ${form.role}. Setup email sent.` });
      onSaved(data.dev_setup_url, data);
      setForm({ name: "", email: "", role: "team_manager", title: "", is_active: true });
      onClose();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader><DialogTitle className="font-display text-3xl">Create user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label className="overline text-muted-foreground">Name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-2 h-12" data-testid="new-user-name" />
          </div>
          <div>
            <Label className="overline text-muted-foreground">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-none mt-2 h-12" data-testid="new-user-email" />
          </div>
          <div>
            <Label className="overline text-muted-foreground">Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger className="rounded-none mt-2 h-12" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => <SelectItem key={r.role} value={r.role}>{ROLE_LABELS[r.role] || r.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-muted-foreground">Title (optional)</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none mt-2 h-12" />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-accent hover:bg-accent/90" data-testid="save-new-user">
              {saving ? "Creating…" : "Create user"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditUserDialog = ({ user, onClose, onSaved, roles, currentUser }) => {
  const [form, setForm] = useState({ name: "", role: "", title: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name, role: user.role, title: user.title || "" });
  }, [user]);

  if (!user) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canChangeRole = currentUser.role === "super_admin";
  const availableRoles = roles.filter((r) => currentUser.role === "super_admin" || !["super_admin", "admin"].includes(r.role));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/users/${user.user_id}`, form);
      toast.success("User updated");
      onSaved();
    } catch (e) {
      toast.error("Failed", { description: e.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg rounded-none">
        <DialogHeader><DialogTitle className="font-display text-3xl">Edit user</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label className="overline text-muted-foreground">Name</Label>
            <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none mt-2 h-12" data-testid="edit-user-name" />
          </div>
          <div>
            <Label className="overline text-muted-foreground">Email (immutable)</Label>
            <Input value={user.email} disabled className="rounded-none mt-2 h-12 bg-muted" />
          </div>
          <div>
            <Label className="overline text-muted-foreground">Role {!canChangeRole && "(super_admin only)"}</Label>
            <Select value={form.role} onValueChange={(v) => set("role", v)} disabled={!canChangeRole}>
              <SelectTrigger className="rounded-none mt-2 h-12" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableRoles.map((r) => <SelectItem key={r.role} value={r.role}>{ROLE_LABELS[r.role] || r.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline text-muted-foreground">Title</Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none mt-2 h-12" />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-none bg-accent hover:bg-accent/90" data-testid="save-edit-user">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserManagement;
